import React, { useState, useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  Plus,
  Edit,
  Trash2,
  Shield,
  Search,
  RefreshCw,
  Key,
  Lock,
  Building2,
  AlertTriangle
} from 'lucide-react';
import { usersApi } from '@domains/admin/api/users.api';
import { notify } from '@core/notifications/notify';
import { useModal, ConfirmModal, UserTypeModal } from '@components/modal';
import PageInfoButton from '@/components/PageInfoButton';

interface UserType {
  id: number;
  name: string;
  description: string;
  permissions: string[];
  created_by: number;
  company_id: number | null;
  created_at: string;
  is_active?: number;
  is_system_profile?: number | boolean;
}

// Profiles that belong to the company-admin scope and are managed
// inside companies by Company Admins — hidden from the system admin profiles page.
const COMPANY_MANAGED_PROFILES = ['Internal Auditor', 'Compliance User'];

// System-level profiles that should never be shown in company context
const SYSTEM_LEVEL_PROFILES = ['External Auditor', 'System Admin'];

interface ProfileGridProps {
  profiles: UserType[];
  canEdit: boolean;
  onEdit: (profile: UserType) => void;
  onDelete: (profile: UserType) => void;
}

const ProfileGrid: React.FC<ProfileGridProps> = ({ profiles, canEdit, onEdit, onDelete }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {profiles.map((userType) => {
      const isActive = userType.is_active !== 0;
      return (
        <div
          key={userType.id}
          className={`rounded-xl border overflow-hidden ${
            isActive
              ? 'bg-white border-slate-200'
              : 'bg-slate-50 border-slate-200 opacity-75'
          }`}
        >
          <div className="p-5">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                  isActive ? 'bg-blue-600' : 'bg-slate-400'
                }`}>
                  <Shield className="text-white" size={18} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className={`text-sm font-bold ${isActive ? 'text-slate-900' : 'text-slate-700'}`}>
                      {userType.name}
                    </h3>
                    {!isActive && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-700">
                        Inactive
                      </span>
                    )}
                    {!userType.company_id && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
                        <Lock size={10} className="inline mr-0.5" />
                        Template
                      </span>
                    )}
                  </div>
                  {userType.description && (
                    <p className={`text-xs mt-1 ${isActive ? 'text-slate-500' : 'text-slate-400'}`}>
                      {userType.description}
                    </p>
                  )}
                </div>
              </div>
              {canEdit && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onEdit(userType)}
                    className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors"
                    title="Edit Profile"
                  >
                    <Edit size={15} />
                  </button>
                  <button
                    onClick={() => onDelete(userType)}
                    className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                    title="Delete Profile"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500">
                  {userType.permissions.length} permissions
                </span>
                {userType.company_id ? (
                  <span className="px-2 py-1 rounded-md font-bold text-[10px] bg-blue-100 text-blue-800">
                    <Building2 size={10} className="inline mr-1" />
                    Company
                  </span>
                ) : (
                  <span className="px-2 py-1 rounded-md font-bold text-[10px] bg-emerald-100 text-emerald-800">
                    <Lock size={10} className="inline mr-1" />
                    System
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-1.5">
                {userType.permissions.slice(0, 6).map((permission) => (
                  <span
                    key={permission}
                    className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-600"
                  >
                    <Key size={9} className="mr-1" />
                    {permission}
                  </span>
                ))}
                {userType.permissions.length > 6 && (
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-600">
                    +{userType.permissions.length - 6} more
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

const PermissionProfiles: React.FC = () => {
  const { hasPermission, user: currentUser, userDomain } = useAuth();
  const { openModal } = useModal();

  const isCompanyDomain = userDomain === 'company';

  const [permissionProfiles, setPermissionProfiles] = useState<UserType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadPermissionProfiles();
  }, []);

  const loadPermissionProfiles = async () => {
    try {
      setIsLoading(true);
      const result = await usersApi.getUserTypes(true); // Include all profiles for admin visibility
      // Company admins should NOT see platform-level profiles (System Admin,
      // Partner Admin) on their permission-profiles page — those are managed
      // by the platform, not by individual companies. Hiding here is UI-only;
      // the backend should also enforce this on save.
      const PLATFORM_PROFILES = new Set(['System Admin', 'Partner Admin']);
      const filtered = isCompanyDomain
        ? (result as any[]).filter((p: any) => !PLATFORM_PROFILES.has(String(p.name)))
        : result;
      setPermissionProfiles(filtered as any);
    } catch (error) {
      console.error('Error loading permission profiles:', error);
      notify.error('Failed to load permission profiles');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUserType = () => {
    openModal({
      component: ({ onClose }: { onClose: () => void }) => (
        <UserTypeModal
          onClose={onClose}
          onSubmit={async (userTypeData: any) => {
            try {
              await usersApi.createUserType({
                ...userTypeData,
                companyId: currentUser?.company_id
              });
              notify.success('Permission profile created successfully');
              loadPermissionProfiles();
              onClose();
            } catch (error) {
              console.error('Error creating permission profile:', error);
              notify.error('Failed to create permission profile');
              throw error;
            }
          }}
        />
      )
    });
  };

  const handleEditUserType = (userType: UserType) => {
    openModal({
      component: ({ onClose }: { onClose: () => void }) => (
        <UserTypeModal
          onClose={onClose}
          selectedUserType={userType}
          onSubmit={async (userTypeData: any) => {
            try {
              await usersApi.updateUserType(userType.id, userTypeData);
              notify.success('Permission profile updated successfully');
              loadPermissionProfiles();
              onClose();
            } catch (error) {
              console.error('Error updating permission profile:', error);
              notify.error('Failed to update permission profile');
              throw error;
            }
          }}
        />
      )
    });
  };

  const handleDeleteUserType = (userType: UserType) => {
    openModal({
      component: ({ onClose }: { onClose: () => void }) => (
        <ConfirmModal
          title="Delete Permission Profile"
          message={`Are you sure you want to delete "${userType.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={async () => {
            try {
              await usersApi.deleteUserType(userType.id);
              notify.success('Permission profile deleted successfully');
              loadPermissionProfiles();
              onClose();
            } catch (error) {
              console.error('Error deleting permission profile:', error);
              notify.error('Failed to delete permission profile');
            }
          }}
          onCancel={onClose}
        />
      )
    });
  };



  const isSystemAdmin = !currentUser?.company_id;

  const matchesSearch = (profile: UserType) =>
    profile.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    profile.description?.toLowerCase().includes(searchTerm.toLowerCase());

  // For system admins: hide company-managed profiles (Compliance User, Internal Auditor).
  // For company admins: split into global templates (read-only) and own company profiles (editable).
  // Also hide system-level profiles that should never appear in company context.
  const filteredPermissionProfiles = isSystemAdmin
    ? permissionProfiles.filter(p => !COMPANY_MANAGED_PROFILES.includes(p.name) && matchesSearch(p))
    : permissionProfiles.filter(p =>
        !(p.is_system_profile === 1 || p.is_system_profile === true) &&
        !SYSTEM_LEVEL_PROFILES.includes(p.name) &&
        matchesSearch(p)
      );

  // Company admin: split filtered list into two buckets.
  const globalTemplates = !isSystemAdmin
    ? filteredPermissionProfiles.filter(p => !p.company_id)
    : [];
  const companyProfiles = !isSystemAdmin
    ? filteredPermissionProfiles.filter(p => !!p.company_id)
    : [];

  // Analytics calculations (based on all profiles shown)
  const analytics = {
    total: filteredPermissionProfiles.length,
    systemProfiles: filteredPermissionProfiles.filter(u => !u.company_id).length,
    companyProfiles: filteredPermissionProfiles.filter(u => u.company_id).length,
    totalPermissions: filteredPermissionProfiles.reduce((sum, ut) => sum + ut.permissions.length, 0),
    avgPermissions: filteredPermissionProfiles.length > 0 ? Math.round(filteredPermissionProfiles.reduce((sum, ut) => sum + ut.permissions.length, 0) / filteredPermissionProfiles.length) : 0,
  };

  if (!hasPermission('users.read')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-white rounded-3xl border border-slate-100 p-8">
          <AlertTriangle className="text-red-500 mb-3" size={36} />
          <h1 className="text-lg font-bold text-slate-900 mb-1">Access Denied</h1>
          <p className="text-slate-500 text-sm">You don't have permission to view {isCompanyDomain ? 'company ' : ''}permission profiles.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">

        {/* Page Heading */}
        <div className="mb-8 text-left">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900 text-left">Permission Profiles</h1><PageInfoButton /></div>
              <p className="text-slate-500 text-left">
                {isCompanyDomain
                  ? `View ${analytics.total} permission profiles available for your company users.`
                  : `Manage ${analytics.total} permission profiles and their associated permissions.`
                }
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={loadPermissionProfiles}
                className="px-4 py-2.5 bg-white border border-slate-200 text-slate-600 font-medium text-xs rounded-full hover:bg-slate-50 transition-colors flex items-center gap-2"
              >
                <RefreshCw size={14} />
                Refresh
              </button>
              {hasPermission('users.create') && (
                <button
                  onClick={handleCreateUserType}
                  className="px-5 py-2.5 bg-blue-600 text-white font-bold text-sm rounded-full hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-lg shadow-blue-200"
                >
                  <Plus size={14} />
                  Create Profile
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stats — glass cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <p className="text-xs text-slate-400 font-bold mb-1">TOTAL</p>
            <p className="text-3xl font-bold text-slate-900">{analytics.total}</p>
            <p className="text-[10px] text-slate-400">All profiles</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <p className="text-xs text-slate-400 font-bold mb-1">SYSTEM</p>
            <p className="text-3xl font-bold text-slate-900">{analytics.systemProfiles}</p>
            <p className="text-[10px] text-slate-400">Platform-wide</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <p className="text-xs text-slate-400 font-bold mb-1">COMPANY</p>
            <p className="text-3xl font-bold text-slate-900">{analytics.companyProfiles}</p>
            <p className="text-[10px] text-slate-400">Company-specific</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <p className="text-xs text-slate-400 font-bold mb-1">AVG PERMS</p>
            <p className="text-3xl font-bold text-slate-900">{analytics.avgPermissions}</p>
            <p className="text-[10px] text-slate-400">Per profile</p>
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Search by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all"
            />
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="px-4 py-2.5 bg-white border border-slate-200 text-slate-600 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors"
            >
              Clear
            </button>
          )}
        </div>

        {/* Permission Profiles Grid */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <RefreshCw className="text-blue-600 animate-spin" size={40} />
            <p className="text-slate-500 text-sm mt-3">Loading permission profiles...</p>
          </div>
        ) : isSystemAdmin ? (
          /* System admin: flat grid */
          filteredPermissionProfiles.length === 0 ? (
            <div className="py-12">
              <Shield className="text-slate-300 mb-3" size={40} />
              <h3 className="text-base font-semibold text-slate-900 mb-1">No permission profiles found</h3>
              <p className="text-slate-500 text-sm max-w-md mx-auto">
                {searchTerm ? 'Try adjusting your search criteria' : 'Get started by creating your first permission profile'}
              </p>
              {hasPermission('users.create') && !searchTerm && (
                <div className="mt-4">
                  <button
                    onClick={handleCreateUserType}
                    className="px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus size={16} className="inline mr-1.5" />
                    Create Permission Profile
                  </button>
                </div>
              )}
            </div>
          ) : (
            <ProfileGrid profiles={filteredPermissionProfiles} canEdit={hasPermission('users.update')} onEdit={handleEditUserType} onDelete={handleDeleteUserType} />
          )
        ) : (
          /* Company admin: two sections */
          <div className="space-y-8">
            {/* Default Templates (read-only) */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Lock className="text-slate-400" size={16} />
                <div>
                  <h2 className="text-sm font-bold text-slate-900">Default Templates</h2>
                  <p className="text-xs text-slate-500">Platform-provided profiles -- read-only, available to all companies</p>
                </div>
              </div>
              {globalTemplates.length === 0 ? (
                <p className="text-slate-500 text-sm py-4">No templates match your search.</p>
              ) : (
                <ProfileGrid profiles={globalTemplates} canEdit={false} onEdit={handleEditUserType} onDelete={handleDeleteUserType} />
              )}
            </div>

            {/* Your Company Profiles (editable) */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="text-blue-600" size={16} />
                <div>
                  <h2 className="text-sm font-bold text-slate-900">Your Company Profiles</h2>
                  <p className="text-xs text-slate-500">Profiles created for your company -- you can edit and delete these</p>
                </div>
              </div>
              {companyProfiles.length === 0 ? (
                <div className="py-8 text-center border border-dashed border-slate-200 rounded-xl">
                  <p className="text-slate-500 text-sm mb-3">
                    {searchTerm ? 'No company profiles match your search.' : 'No company-specific profiles yet.'}
                  </p>
                  {hasPermission('users.create') && !searchTerm && (
                    <button
                      onClick={handleCreateUserType}
                      className="px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                    >
                      <Plus size={16} />
                      Create Profile
                    </button>
                  )}
                </div>
              ) : (
                <ProfileGrid profiles={companyProfiles} canEdit={hasPermission('users.update')} onEdit={handleEditUserType} onDelete={handleDeleteUserType} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PermissionProfiles;
